/**
 * Renders "Find more like this" exemplar boxes as green outlines (orange for boxes to ignore),
 * distinct from note geometry (purple, see ObservationMap's NoteGeometryRenderer) and the
 * in-progress draw feature. Boxes being composed are numbered to match their sidebar chips, red
 * when they'd be refused, and share hover with those chips. A read-only instance shows the boxes a
 * finished run was given.
 */

import * as turf from '@turf/turf';
import React from 'react';

import {
  ExemplarPolarity,
  ExemplarProblem,
  ExemplarRef,
  isSameExemplar,
} from 'app/components/AgentExplorer/fewShotExemplarTypes';
import MapContent from 'app/components/DeclarativeMap/MapContent';
import colors from 'app/styles/colors.json';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as makeLayers from 'app/utils/mapUtils/makeLayers';

interface CommonProps {
  map: mapboxgl.Map;
  isMapLoaded: boolean;
  exemplars: GeoJSON.Polygon[];
  negativeExemplars: GeoJSON.Polygon[];
  // Keeps source/layer ids apart when more than one instance is on the map.
  idSuffix?: string;
}

interface DraftProps extends CommonProps {
  readOnly?: false;
  problems: Record<ExemplarPolarity, (ExemplarProblem | null)[]>;
  hovered: ExemplarRef | null;
  onHoverExemplar: (ref: ExemplarRef | null) => void;
  // The box being edited is drawn by the edit control instead, so it's left out here.
  editing: ExemplarRef | null;
  // Null while drawing, so a drag that starts on an existing box draws rather than opens it.
  onClickExemplar: ((ref: ExemplarRef) => void) | null;
}

// A finished run's boxes: drawn the same way, but with no hover, click, or problem state.
interface ReadOnlyProps extends CommonProps {
  readOnly: true;
}

type Props = DraftProps | ReadOnlyProps;

function layerIds(idSuffix: string | undefined) {
  const suffix = idSuffix ? `-${idSuffix}` : '';
  return {
    source: `few-shot-exemplar-source${suffix}`,
    line: `few-shot-exemplar-layer${suffix}`,
    hit: `few-shot-exemplar-hit-layer${suffix}`,
    badge: `few-shot-exemplar-badge-layer${suffix}`,
  };
}

const IS_INVALID: mapboxgl.ExpressionSpecification = ['==', ['get', 'invalid'], true];
const IS_HOVERED: mapboxgl.ExpressionSpecification = ['==', ['get', 'hovered'], true];
const IS_NEGATIVE: mapboxgl.ExpressionSpecification = ['==', ['get', 'polarity'], 'negative'];
const BOX_COLOR: mapboxgl.ExpressionSpecification = [
  'case',
  IS_INVALID,
  colors.red,
  IS_NEGATIVE,
  colors.orange,
  colors.green,
];

function exemplarRefFromEvent(ev: mapboxgl.MapLayerMouseEvent): ExemplarRef | null {
  const properties = ev.features?.[0]?.properties;
  const polarity = properties?.polarity;
  const index = properties?.index;
  return (polarity === 'positive' || polarity === 'negative') && typeof index === 'number'
    ? {polarity, index}
    : null;
}

function topLeftCorner(polygon: GeoJSON.Polygon): [number, number] {
  const [minX, , , maxY] = turf.bbox(polygon);
  return [minX, maxY];
}

const FewShotExemplarLayer: React.FC<Props> = (props) => {
  const {map, isMapLoaded, exemplars, negativeExemplars, idSuffix} = props;
  const draft = props.readOnly ? null : props;
  const problems = draft?.problems ?? null;
  const hovered = draft?.hovered ?? null;
  const onHoverExemplar = draft?.onHoverExemplar ?? null;
  const editing = draft?.editing ?? null;
  const onClickExemplar = draft?.onClickExemplar ?? null;
  const ids = React.useMemo(() => layerIds(idSuffix), [idSuffix]);

  React.useEffect(() => {
    if (!onHoverExemplar) {
      return;
    }
    const onMouseMove = (ev: mapboxgl.MapLayerMouseEvent) => {
      if (ev.originalEvent.buttons === 1) return; // mid-drag, e.g. drawing the next box
      onHoverExemplar(exemplarRefFromEvent(ev));
    };
    const onMouseLeave = () => onHoverExemplar(null);
    map.on('mousemove', ids.hit, onMouseMove);
    map.on('mouseleave', ids.hit, onMouseLeave);
    return () => {
      map.off('mousemove', ids.hit, onMouseMove);
      map.off('mouseleave', ids.hit, onMouseLeave);
    };
  }, [map, onHoverExemplar, ids]);

  React.useEffect(() => {
    if (!onClickExemplar) {
      return;
    }
    const onClick = (ev: mapboxgl.MapLayerMouseEvent) => {
      const ref = exemplarRefFromEvent(ev);
      if (ref) {
        onClickExemplar(ref);
      }
    };
    map.on('click', ids.hit, onClick);
    return () => {
      map.off('click', ids.hit, onClick);
    };
  }, [map, onClickExemplar, ids]);

  const source = React.useMemo(() => {
    const featuresOf = (polarity: ExemplarPolarity, polygons: GeoJSON.Polygon[]) =>
      polygons.flatMap((polygon, index) => {
        const ref = {polarity, index};
        const invalid = problems?.[polarity][index] != null;
        return isSameExemplar(ref, editing)
          ? []
          : [
              geoJsonUtils.feature(polygon, {
                polarity,
                index,
                invalid,
                hovered: isSameExemplar(ref, hovered),
              }),
              // The number is pinned to one point in the box's top-left corner. Labelling the
              // polygon itself repeats the number once per map tile the box crosses.
              geoJsonUtils.feature(
                {type: 'Point', coordinates: topLeftCorner(polygon)},
                {
                  polarity,
                  n: polarity === 'negative' ? `-${index + 1}` : String(index + 1),
                  invalid,
                }
              ),
            ];
      });
    return {
      id: ids.source,
      source: {
        type: 'geojson' as const,
        data: {
          type: 'FeatureCollection' as const,
          features: [
            ...featuresOf('positive', exemplars),
            ...featuresOf('negative', negativeExemplars),
          ],
        },
      },
    };
  }, [exemplars, negativeExemplars, problems, hovered, editing, ids]);

  const isReadOnly = draft === null;
  const layers = React.useMemo(
    () => [
      ...(isReadOnly
        ? []
        : [
            {
              id: ids.hit,
              ...makeLayers.makeFill({
                fillColor: BOX_COLOR,
                // Transparent fills still receive mouse events; this layer is the hover target.
                opacity: ['case', IS_HOVERED, 0.2, 0],
                filter: ['has', 'index'],
              }),
              source: ids.source,
            },
            {
              id: ids.badge,
              type: 'symbol' as const,
              source: ids.source,
              minzoom: 2,
              maxzoom: 22,
              filter: ['has', 'n'],
              layout: {
                'text-field': ['get', 'n'],
                'text-size': 14,
                'text-anchor': 'top-left',
                'text-offset': [0.4, 0.2],
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              },
              paint: {
                'text-color': '#fff',
                'text-halo-color': BOX_COLOR,
                'text-halo-width': 2,
              },
            },
          ]),
      {
        id: ids.line,
        ...makeLayers.makeLine({
          color: BOX_COLOR,
          width: ['case', IS_HOVERED, 4, 2],
          opacity: 1,
        }),
        source: ids.source,
      },
    ],
    [isReadOnly, ids]
  );

  if (exemplars.length + negativeExemplars.length === 0) {
    return null;
  }

  return (
    <MapContent
      map={map}
      isMapLoaded={isMapLoaded}
      sources={[source]}
      layers={layers as mapboxgl.LayerSpecification[]}
    />
  );
};

export default FewShotExemplarLayer;
